import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { fetchMeetingParticipants } from '../api/meetings';
import { resolvePublicMediaUrl } from '../config/api';
import { MemberAvatarOrPhoto } from './MemberCard';

/**
 * @typedef {import('../api/meetings').MeetingParticipant} MeetingParticipant
 */

/**
 * @param {string | null | undefined} fullName
 */
function splitFullName(fullName) {
    const parts = String(fullName || '')
        .trim()
        .split(/\s+/)
        .filter(Boolean);
    if (parts.length === 0) {
        return { givenName: '', familyName: '' };
    }
    if (parts.length === 1) {
        return { givenName: parts[0], familyName: '' };
    }
    return {
        givenName: parts[0],
        familyName: parts.slice(1).join(' '),
    };
}

/**
 * @param {{
 *   meetingId: string,
 *   authToken: string,
 *   refreshKey?: number,
 * }} props
 */
export function MeetingParticipantsPanel({
    meetingId,
    authToken,
    refreshKey = 0,
}) {
    const { t } = useTranslation();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(/** @type {string | null} */ (null));
    const [participants, setParticipants] = useState(
        /** @type {MeetingParticipant[]} */ ([]),
    );

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setError(null);

        (async () => {
            try {
                const list = await fetchMeetingParticipants(
                    authToken,
                    meetingId,
                );
                if (cancelled) return;
                setParticipants(list.participants);
            } catch (err) {
                if (cancelled) return;
                setParticipants([]);
                setError(
                    err instanceof Error
                        ? err.message
                        : t('eventCalendarPage.participantsErrorGeneric'),
                );
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [meetingId, authToken, refreshKey, t]);

    return (
        <section
            className="event-calendar-participants mt-4 pt-3"
            aria-label={t('eventCalendarPage.participantsTitle')}
        >
            <h3 className="event-calendar-detail-section-title h6 mb-3">
                {t('eventCalendarPage.participantsTitle')}
            </h3>

            {loading ? (
                <p
                    className="event-calendar-participants-loading small text-secondary mb-0"
                    role="status"
                    aria-live="polite"
                >
                    {t('eventCalendarPage.participantsLoading')}
                </p>
            ) : error ? (
                <p
                    className="app-modal-error small text-danger mb-0"
                    role="alert"
                >
                    {error}
                </p>
            ) : participants.length === 0 ? (
                <p className="event-calendar-participants-empty small text-secondary mb-0">
                    {t('eventCalendarPage.participantsEmpty')}
                </p>
            ) : (
                <ul className="event-calendar-participants-list list-unstyled mb-0">
                    {participants.map((participant) => {
                        const displayName =
                            participant.fullName?.trim() || '—';
                        const { givenName, familyName } = splitFullName(
                            participant.fullName,
                        );
                        const avatar = (
                            <MemberAvatarOrPhoto
                                givenName={givenName}
                                familyName={familyName}
                                imageUrl={
                                    participant.profilePictureUrl
                                        ? resolvePublicMediaUrl(
                                              participant.profilePictureUrl,
                                          )
                                        : undefined
                                }
                                className="event-calendar-participant-avatar"
                            />
                        );

                        return (
                            <li
                                key={participant.personId}
                                className="event-calendar-participant-item"
                            >
                                <div className="event-calendar-participant-main">
                                    {participant.personId ? (
                                        <Link
                                            to={`/member-area/members/${encodeURIComponent(participant.personId)}`}
                                            className="event-calendar-participant-avatar-link"
                                        >
                                            {avatar}
                                        </Link>
                                    ) : (
                                        avatar
                                    )}
                                    <div className="min-w-0 flex-grow-1">
                                        {participant.personId ? (
                                            <Link
                                                to={`/member-area/members/${encodeURIComponent(participant.personId)}`}
                                                className="event-calendar-participant-name"
                                            >
                                                {displayName}
                                            </Link>
                                        ) : (
                                            <span className="event-calendar-participant-name">
                                                {displayName}
                                            </span>
                                        )}
                                        {participant.typeName ||
                                        participant.parentFullName ? (
                                            <div className="event-calendar-participant-meta">
                                                {participant.typeName ? (
                                                    <span className="event-calendar-participant-type-badge">
                                                        {
                                                            participant.typeName
                                                        }
                                                    </span>
                                                ) : null}
                                                {participant.parentFullName ? (
                                                    <span className="event-calendar-participant-parent text-secondary">
                                                        {t(
                                                            'eventCalendarPage.participantParent',
                                                            {
                                                                name: participant.parentFullName,
                                                            },
                                                        )}
                                                    </span>
                                                ) : null}
                                            </div>
                                        ) : null}
                                    </div>
                                </div>
                                {participant.guests.length > 0 ? (
                                    <ul className="event-calendar-participant-guests list-unstyled mb-0">
                                        {participant.guests.map((guest) => (
                                            <li
                                                key={guest.id}
                                                className="event-calendar-participant-guest small text-secondary"
                                            >
                                                {t(
                                                    'eventCalendarPage.participantGuest',
                                                    {
                                                        name:
                                                            guest.fullName ||
                                                            `${guest.firstName} ${guest.lastName}`.trim(),
                                                    },
                                                )}
                                            </li>
                                        ))}
                                    </ul>
                                ) : null}
                            </li>
                        );
                    })}
                </ul>
            )}
        </section>
    );
}
